import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { SPP_REPORTS_BY_PROGRAM_TYPE } from '@graphql/sppReports.gql';
import { LOG_EXPORT_AUDIT_EVENT } from 'graphql/audit.gql';
import { exportToExcelMultiSheet, ExportColumn, ExportSheet } from '@utils/excelExport';
import { useSelectedPeriod } from '@context/SelectedPeriodContext';

export type SPPMeterData = {
  meter_id: number;
  meter_number: string;
  location: string;
  company: string;
  generation_type: string;
  jan?: number | null;
  feb?: number | null;
  mar?: number | null;
  apr?: number | null;
  may?: number | null;
  jun?: number | null;
  jul?: number | null;
  aug?: number | null;
  sep?: number | null;
  oct?: number | null;
  nov?: number | null;
  dec?: number | null;
  ytd_total: number;
};

export type SPPReportsData = {
  ippMeters: SPPMeterData[];
  comfitMeters: SPPMeterData[];
  maxcapMeters: SPPMeterData[];
  communitySolarMeters: SPPMeterData[];
};

export const useSppReportsInvoiceAmountsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { selectedYear: ctxYear, setYear } = useSelectedPeriod();
  const [logExportAudit] = useMutation(LOG_EXPORT_AUDIT_EVENT);

  // URL param takes precedence, then context year (if set), then current year
  const yearParam = searchParams.get('year');
  const currentYear = yearParam ? Number.parseInt(yearParam, 10) : (ctxYear ?? new Date().getFullYear());

  // Disable next button if we're at current year
  const currentDate = new Date();
  const isNextYearDisabled = currentYear >= currentDate.getFullYear();

  // Navigate to a different year
  const navigateYear = (direction: number) => {
    const newYear = currentYear + direction;
    const params = new URLSearchParams(searchParams);
    params.set('year', newYear.toString());
    setYear(newYear);

    navigate(
      {
        pathname: location.pathname,
        search: `?${params.toString()}`,
      },
      { replace: false }
    );
  };

  // Fetch data using GraphQL query
  const { data: queryData, loading, error } = useQuery(SPP_REPORTS_BY_PROGRAM_TYPE, {
    variables: {
      year: currentYear,
    },
    fetchPolicy: 'cache-and-network',
  });

  const data: SPPReportsData = {
    ippMeters: queryData?.sppReportsByProgramType?.ipp_meters || [],
    comfitMeters: queryData?.sppReportsByProgramType?.comfit_meters || [],
    maxcapMeters: queryData?.sppReportsByProgramType?.maxcap_meters || [],
    communitySolarMeters: queryData?.sppReportsByProgramType?.community_solar_meters || [],
  };

  const handleExportToExcel = () => {
    const exportColumns: ExportColumn[] = [
      { header: 'Company', dataIndex: 'company', format: 'text' },
      { header: 'Name', dataIndex: 'location', format: 'text' },
      { header: 'Meter Number', dataIndex: 'meter_number', format: 'text' },
      { header: 'Generation Type', dataIndex: 'generation_type', format: 'text' },
      { header: 'Jan', dataIndex: 'jan', format: 'currency' },
      { header: 'Feb', dataIndex: 'feb', format: 'currency' },
      { header: 'Mar', dataIndex: 'mar', format: 'currency' },
      { header: 'Apr', dataIndex: 'apr', format: 'currency' },
      { header: 'May', dataIndex: 'may', format: 'currency' },
      { header: 'Jun', dataIndex: 'jun', format: 'currency' },
      { header: 'Jul', dataIndex: 'jul', format: 'currency' },
      { header: 'Aug', dataIndex: 'aug', format: 'currency' },
      { header: 'Sep', dataIndex: 'sep', format: 'currency' },
      { header: 'Oct', dataIndex: 'oct', format: 'currency' },
      { header: 'Nov', dataIndex: 'nov', format: 'currency' },
      { header: 'Dec', dataIndex: 'dec', format: 'currency' },
      { header: 'YTD Total', dataIndex: 'ytd_total', format: 'currency' },
    ];

    const sheets: ExportSheet<SPPMeterData>[] = [
      { name: 'IPP', data: data.ippMeters, columns: exportColumns },
      { name: 'COMFIT', data: data.comfitMeters, columns: exportColumns },
      { name: 'AGC Wind Farm', data: data.maxcapMeters, columns: exportColumns },
      { name: 'Community Solar', data: data.communitySolarMeters, columns: exportColumns },
    ];

    exportToExcelMultiSheet(sheets, `SPP_Invoice_Amounts_${currentYear}.xlsx`);

    logExportAudit({
      variables: { dashboard: 'SppReportsInvoiceAmounts', monthYear: String(currentYear) },
    }).catch((err) => console.error('Failed to log export audit event:', err));
  };

  return {
    currentYear,
    navigateYear,
    isNextYearDisabled,
    data,
    loading,
    error,
    handleExportToExcel,
  };
};
